import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot';
import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons/faArrowUpRightFromSquare';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from 'FrontRoyalAngular';
import { useTargetBrandConfig } from 'FrontRoyalAngular/useTargetBrandConfig';
import { useSyncConfig } from 'FrontRoyalConfig/useConfig';
import { type CamelCasedStudentNetworkEvent } from 'StudentNetworkEvent';
import { OuiBuilder, OuiEvent } from 'OuiCalendarEvent';
import { saveAs } from 'file-saver';
import { addEventToDeviceCalendar } from 'DeviceCalendar';
import { isOnlineEvent } from '../../utils/eventHelpers';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

type CalendarMethod = 'google' | 'office365' | 'outlookLive' | 'ics' | 'device';

type EventDetailSidebarProps = {
    event: CamelCasedStudentNetworkEvent;
};

function isSameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getTimezoneAbbr(date: Date, timeZone: string) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(date);
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
}

function formatDateRange(event: CamelCasedStudentNetworkEvent, timeZone: string | undefined) {
    if (event.dateTbd) return event.dateTbdDescription ?? 'Date TBD';
    if (!event.startTime) return '';

    const start = new Date(event.startTime * 1000);
    const endTime = (event as Record<string, unknown>).endTime as number | undefined;
    const end = endTime ? new Date(endTime * 1000) : null;

    const dateOpts: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone,
    };
    const timeOpts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', timeZone };

    const dateStr = start.toLocaleDateString('en-US', dateOpts);
    const startTimeStr = start.toLocaleTimeString('en-US', timeOpts);
    const tz = timeZone ? getTimezoneAbbr(start, timeZone) : '';

    if (end && isSameDay(start, end)) {
        const endTimeStr = end.toLocaleTimeString('en-US', timeOpts);
        return `${dateStr}\n${startTimeStr} – ${endTimeStr} ${tz}`;
    }
    if (end) {
        const endDateStr = end.toLocaleDateString('en-US', dateOpts);
        const endTimeStr = end.toLocaleTimeString('en-US', timeOpts);
        return `${dateStr}\n${startTimeStr} ${tz} –\n${endDateStr}\n${endTimeStr} ${tz}`;
    }
    return `${dateStr}\n${startTimeStr} ${tz}`;
}

type EventDetailSidebarPresentationProps = {
    event: CamelCasedStudentNetworkEvent;
    timeZone: string | undefined;
    googleApiKey: string | undefined;
    calendarOpen: boolean;
    dropUp: boolean;
    onToggleCalendar: () => void;
    onCalendarOption: (method: CalendarMethod) => void;
    calendarRef: React.RefObject<HTMLDivElement | null>;
};

// oxlint-disable-next-line max-lines-per-function, complexity -- sidebar with calendar dropdown, RSVP, info box, and map
export function EventDetailSidebarPresentation({
    event,
    timeZone,
    googleApiKey,
    calendarOpen,
    dropUp,
    onToggleCalendar,
    onCalendarOption,
    calendarRef,
}: EventDetailSidebarPresentationProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.eventDetail' });
    const extra = event as Record<string, unknown>;
    const externalRsvpUrl = extra.externalRsvpUrl as string | undefined;
    const dateTbd = event.dateTbd;
    const online = isOnlineEvent(event.eventType);
    const locationName = event.anonymized ? undefined : event.locationName;
    const placeDetails = extra.placeDetails as Record<string, unknown> | undefined;
    const address = placeDetails?.formatted_address as string | undefined;
    const lat = placeDetails?.lat as number | undefined;
    const lng = placeDetails?.lng as number | undefined;
    const hasMap = !online && lat != null && lng != null && !!googleApiKey;
    const endTime = extra.endTime as number | undefined;
    const isPast = (endTime ?? event.startTime ?? 0) * 1000 < Date.now();
    const mapCenter = useMemo(() => (hasMap ? { lat: lat!, lng: lng! } : undefined), [hasMap, lat, lng]);

    const rsvpButton = externalRsvpUrl && (
        <a
            href={externalRsvpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-[50px] items-center justify-center gap-2 rounded-[8px] bg-primary text-lg font-semibold text-white transition-opacity hover:opacity-85"
        >
            {t('register')}
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-base" />
        </a>
    );

    return (
        <div className="flex w-full flex-col lg:w-[341px]">
            {/* RSVP button — desktop: top, mobile: bottom (after add to calendar) */}
            {rsvpButton && <div className="mb-5 hidden lg:block">{rsvpButton}</div>}

            {/* Info box — Sketch: #f6f7f9, top corners 16px, bottom corners too when no map */}
            <div className={`${hasMap ? 'rounded-t-8' : 'rounded-8'} bg-slate-grey-50 p-5`}>
                <div className="mb-4 flex gap-3">
                    <FontAwesomeIcon icon={faClock} className="mt-1 text-[20px] text-slate-grey-300" />
                    <span className="whitespace-pre-wrap text-base leading-[22px] text-black">
                        {formatDateRange(event, timeZone)}
                    </span>
                </div>
                {online ? (
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faVideo} className="text-[20px] text-slate-grey-300" />
                        <span className="text-base leading-[22px] text-black">{t('onlineEvent')}</span>
                    </div>
                ) : (
                    locationName && (
                        <div className="flex items-center gap-3">
                            <FontAwesomeIcon icon={faLocationDot} className="text-[20px] text-slate-grey-300" />
                            <span className="whitespace-pre-wrap text-base leading-[22px] text-black">
                                {locationName}
                                {address && `\n${address}`}
                            </span>
                        </div>
                    )
                )}
            </div>

            {/* Map — clickable, opens Google Maps directions in new tab */}
            {hasMap && mapCenter && (
                <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block cursor-pointer"
                >
                    <APIProvider apiKey={googleApiKey!}>
                        <Map
                            defaultCenter={mapCenter}
                            defaultZoom={13}
                            gestureHandling="none"
                            disableDefaultUI
                            className="pointer-events-none h-[127px] w-full overflow-hidden rounded-b-8"
                        >
                            <Marker position={mapCenter} />
                        </Map>
                    </APIProvider>
                </a>
            )}

            {/* Add to Calendar dropdown — hidden for past events */}
            {!dateTbd && !isPast && (
                <div ref={calendarRef} className="relative mt-5">
                    <button
                        type="button"
                        onClick={onToggleCalendar}
                        className="flex h-[50px] w-full items-center justify-center gap-2 rounded-8 bg-slate-grey-50 text-base text-black transition-colors hover:bg-slate-grey-100"
                    >
                        <FontAwesomeIcon icon={faCalendar} className="text-[20px] text-slate-grey-300" />
                        {t('addToCalendar')}
                        <FontAwesomeIcon icon={faChevronDown} className="text-sm text-black" />
                    </button>
                    {calendarOpen && (
                        <div
                            className={`absolute w-full overflow-hidden rounded-8 bg-white shadow-[0_2px_10px_0_rgba(0,0,0,0.15)] ${
                                dropUp ? 'bottom-full mb-2' : 'top-full mt-2'
                            }`}
                        >
                            {(
                                [
                                    ...(window.CORDOVA ? ([['device', t('deviceCalendar')]] as const) : []),
                                    ['google', t('googleCalendar')],
                                    ['office365', t('office365')],
                                    ['outlookLive', t('outlookCom')],
                                    ...(window.CORDOVA ? [] : ([['ics', t('downloadIcs')]] as const)),
                                ] as Array<readonly [CalendarMethod, string]>
                            ).map(([method, label]) => (
                                <button
                                    key={method}
                                    type="button"
                                    onClick={() => onCalendarOption(method)}
                                    className="flex w-full px-4 py-3 text-start text-base text-black hover:bg-slate-grey-50"
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {rsvpButton && <div className="mt-5 lg:hidden">{rsvpButton}</div>}
        </div>
    );
}

// oxlint-disable-next-line max-lines-per-function -- wrapper with calendar and config hooks
export function EventDetailSidebar({ event }: EventDetailSidebarProps) {
    const currentUser = useCurrentUser();
    const brandConfig = useTargetBrandConfig();
    const config = useSyncConfig();
    const extra = event as Record<string, unknown>;
    const description = extra.description as string | undefined;
    const locationName = event.anonymized ? undefined : event.locationName;
    const placeDetails = extra.placeDetails as Record<string, unknown> | undefined;
    const address = placeDetails?.formatted_address as string | undefined;

    const timeZone = (currentUser?.timezone ?? event.timezone) || undefined;

    const [calendarOpen, setCalendarOpen] = useState(false);
    const [dropUp, setDropUp] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);

    const toggleCalendar = useCallback(() => {
        setCalendarOpen(prev => {
            if (!prev && calendarRef.current) {
                const rect = calendarRef.current.getBoundingClientRect();
                const dropdownHeight = 4 * 44 + 8;
                setDropUp(rect.bottom + dropdownHeight > window.innerHeight);
            }
            return !prev;
        });
    }, []);

    useEffect(() => {
        if (!calendarOpen) return undefined;
        const handler = (e: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) setCalendarOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [calendarOpen]);

    const handleCalendarOption = useCallback(
        (method: CalendarMethod) => {
            const startDate = event.startTime ? new Date(event.startTime * 1000) : new Date();
            const endTime = extra.endTime as number | undefined;
            const endDate = endTime ? new Date(endTime * 1000) : new Date(startDate.getTime() + 60 * 60 * 1000);
            const calEvent = new OuiEvent({
                id: event.id,
                title: event.title,
                startDate,
                endDate,
                description: description ?? '',
                address: [locationName, address].filter(Boolean).join(', '),
                timezone: event.timezone,
            });

            if (method === 'device') {
                addEventToDeviceCalendar(calEvent);
            } else if (method === 'google') {
                window.open(OuiBuilder.google(calEvent), '_blank');
            } else if (method === 'office365') {
                window.open(OuiBuilder.outlook(calEvent, 'office'), '_blank');
            } else if (method === 'outlookLive') {
                window.open(OuiBuilder.outlook(calEvent, 'live'), '_blank');
            } else if (method === 'ics') {
                const blob = OuiBuilder.ics(calEvent, brandConfig.brandNameLong, 'blob') as Blob;
                const filename = `${event.title.replace(/ +/g, '-').toLowerCase()}.ics`;
                saveAs(blob, filename);
            }
            setCalendarOpen(false);
        },
        [event, extra, description, locationName, address, brandConfig.brandNameLong],
    );

    return (
        <EventDetailSidebarPresentation
            event={event}
            timeZone={timeZone}
            googleApiKey={config.google_api_key}
            calendarOpen={calendarOpen}
            dropUp={dropUp}
            onToggleCalendar={toggleCalendar}
            onCalendarOption={handleCalendarOption}
            calendarRef={calendarRef}
        />
    );
}
